import { useEffect, useRef, useId } from 'react';
import { createPortal } from 'react-dom';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * Modal — focus-trapping dialog primitive.
 *
 * Behaviour:
 *   - role="dialog" + aria-modal="true" + aria-labelledby=title id
 *   - Renders via createPortal to document.body so it centres on the
 *     viewport instead of nesting inside the sidebar drawer's DOM subtree.
 *   - Tab cycles forward through focusables; Shift+Tab cycles back —
 *     focus cannot escape to the dashboard underneath.
 *   - Escape closes; focus restores to the element that was active
 *     before the modal opened (typically the trigger button).
 *   - Backdrop click closes (preserves the prior hand-rolled UX).
 */
export default function Modal({ open, onClose, title, children }: ModalProps) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  // Remember the element that owned focus before the modal opened so we
  // can restore it on close. Run as a layout-time effect inside the
  // mount branch below.
  useEffect(() => {
    if (!open) return;
    previouslyFocused.current = (document.activeElement as HTMLElement | null) ?? null;

    // Defer initial focus to the next tick so React's commit phase
    // finishes and the contentRef is populated. Focus the first
    // focusable child if one exists, otherwise the content container.
    const id = window.setTimeout(() => {
      const root = contentRef.current;
      if (!root) return;
      const focusables = root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      const first = focusables[0];
      if (first) first.focus();
      else root.focus();
    }, 0);

    return () => {
      window.clearTimeout(id);
      // Restore focus to the trigger on close. Wrap in a try so a
      // detached/no-longer-visible trigger doesn't throw.
      try {
        previouslyFocused.current?.focus?.();
      } catch {
        /* trigger element gone — silently drop focus */
      }
    };
  }, [open]);

  // Escape close + focus trap (Tab / Shift+Tab cycle).
  useEffect(() => {
    if (!open) return;

    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;

      const root = contentRef.current;
      if (!root) return;

      const focusables = Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
        (el) => !el.hasAttribute('aria-hidden') && el.offsetParent !== null,
      );
      if (focusables.length === 0) {
        // No focusables — keep focus trapped on the content container.
        e.preventDefault();
        root.focus();
        return;
      }

      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      const active = document.activeElement as HTMLElement | null;

      if (e.shiftKey) {
        if (active === first || !root.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  const titleId = useId();

  if (!open) return null;

  const overlay = (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => {
        if (e.target === overlayRef.current) onClose();
      }}
    >
      <div
        ref={contentRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="w-full max-w-md rounded-lg bg-white shadow-xl outline-none dark:bg-warm-dark-surface"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-warm-border-light px-6 py-4 dark:border-warm-dark-border">
          <h3
            id={titleId}
            className="text-lg font-semibold text-warm-gray-900 dark:text-warm-white"
          >
            {title}
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close dialog"
            className="flex h-6 w-6 items-center justify-center rounded text-warm-gray-400 hover:bg-warm-gray-100 hover:text-warm-gray-600 dark:hover:bg-white/10 dark:hover:text-warm-gray-200"
          >
            <svg
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
        <div className="px-6 py-4">{children}</div>
      </div>
    </div>
  );

  // Portal to <body> so the modal centres on the viewport at every
  // width (the Create-Group-stuck-inside-<aside> bug from the 320px
  // audit). SSR safety: only portal once document is defined; on the
  // happy-dom test runner document is always present.
  if (typeof document === 'undefined') return overlay;
  return createPortal(overlay, document.body);
}
